/**
 * @file lib/applicantDeepLink.js
 * @description The "View application" link in agency emails: /dashboard?application=<id>&case=<id>. Mirrors the server
 *   link engine (supabase/functions/_shared/email/links.ts applicantLink), which writes it. The app reads it once on
 *   load, strips it from the address bar, and keeps it in sessionStorage until an agency is signed in, so an agency
 *   that has to sign in first still lands on that applicant. AgencyDashboard opens it through initialApplicantDeepLink
 *   (pipeline, Applicants, that listing selected, the applicant highlighted).
 * @module lib/applicantDeepLink
 */

const KEY = "aba_applicant_link";
const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
let pending = null; // the link captured on this page load (works even when sessionStorage is blocked)

/** { applicationId, caseId } from a query string, or null. Both ids must be uuids; the case alone is enough. */
export function parseApplicantLink(search) {
  const p = new URLSearchParams(search || "");
  const applicationId = p.get("application");
  const caseId = p.get("case");
  const app = applicationId && UUID.test(applicationId) ? applicationId : null;
  const cs = caseId && UUID.test(caseId) ? caseId : null;
  if (!app && !cs) return null;
  return { applicationId: app, caseId: cs };
}

function storage() {
  try { return window.sessionStorage; } catch { return null; }
}

/** Read the link from the address bar (if any), remember it for this tab, and strip it from the URL. */
export function captureApplicantLink() {
  if (typeof window === "undefined") return;
  const link = parseApplicantLink(window.location.search);
  if (!link) return;
  try { storage()?.setItem(KEY, JSON.stringify(link)); } catch { /* private mode: the link still works this load */ }
  const url = new URL(window.location.href);
  url.searchParams.delete("application");
  url.searchParams.delete("case");
  window.history.replaceState({}, "", url.pathname + url.search);
  pending = link;
}

/** The remembered link, once: later calls return null. */
export function takeApplicantLink() {
  captureApplicantLink();
  let link = pending;
  pending = null;
  try {
    const raw = storage()?.getItem(KEY);
    storage()?.removeItem(KEY);
    if (!link && raw) {
      const saved = JSON.parse(raw) || {};
      link = parseApplicantLink(new URLSearchParams({ application: saved.applicationId || "", case: saved.caseId || "" }).toString());
    }
  } catch { /* unreadable storage: use what this load captured */ }
  return link;
}
